import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { defer, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, switchMap, take, timeout } from 'rxjs/operators';

import { RetrievePreferenceAdapter } from '@amfam/billing/shared/util/lib/adapters/billing-retrieve-preference-adapter';
import { UnassociatedRiskAdapterService } from '@amfam/billing/shared/util/lib/adapters/unassociated-risk-adapter.service';
import { env } from '@amfam/environment';
import {
  selectAfiMidvaleBillingAccountNumbers,
  selectPolicySummariesSettledOrSkipped
} from '@amfam/policy/data-access/lib/+state/summaries/summaries.selectors';
import type { ApiStatus, PartialStatus } from '@amfam/shared/models/lib/api/api-status.model';
import type {
  BillAccount,
  BillingPreference,
  DecoratedPreference,
  DeletePreferencesRequest,
  DeletePreferencesResponse,
  PolicyList,
  RetrievePreferencePayload,
  RetrievePreferenceResponse,
  UnassociatedRiskDetailSuccess,
  UpdatePreferencesRequest,
  UpdatePreferencesResponse,
  UpdateRegistrationRequest,
  UpdateRegistrationResponse
} from '@amfam/shared/models/lib/billing/bill-account.model';
import {
  BillAccountRegistration,
  EntityReferences,
  ONLINE_BILLING
} from '@amfam/shared/models/lib/billing/billing-constants';
import { selectMdmId, selectUserState } from '@amfam/shared/user/lib/+state/user.selectors';
import { FeatureFlagService } from '@amfam/shared/utility/feature-flag/data-access/lib/services/feature-flag.service';
import { Applications } from '@amfam/shared/utility/shared-services/lib/application/application.enum';
import { ApplicationService } from '@amfam/shared/utility/shared-services/lib/application/application.service';
import { CopyService } from '@amfam/shared/utility/shared-services/lib/copy/copy.service';
import { requireExpAdminApi } from '@amfam/shared/utility/shared-services/lib/tokens/require-exp-admin-api';
import { deepEqual } from '@amfam/shared/utility/shared-services/lib/utils/object.utils';

@Injectable()
export class BillAccountsService {
  private store = inject(Store);
  private http = inject(HttpClient);
  private retrievepreferenceAdapter = inject(RetrievePreferenceAdapter);
  private unassociatedBaRiskAdapter = inject(UnassociatedRiskAdapterService);
  private copyService = inject(CopyService);
  private applicationService = inject(ApplicationService);
  private featureFlagService = inject(FeatureFlagService);
  private readonly isAdminApp = this.applicationService.isApp(Applications.MYACCOUNT_ADMIN);
  private readonly mdmId = toSignal(this.store.select(selectMdmId));
  private readonly userState = toSignal(this.store.select(selectUserState));

  private getHeaders(): HttpHeaders | null {
    if (this.isAdminApp) return null;
    return new HttpHeaders().set('afi-appname', env.afiAppName).set('afi-api-key', env.afiApiKey);
  }

  /**
   *
   * @returns Temporary method to support AFI/Midvale partners until they are onboarded to EXP API truely. This is just a L7 bypas,gives same response as L7 API.
   */
  public getBillAccountsAfiMidvale() {
    const byWaidEndpoint = this.isAdminApp
      ? `${env.billingExpApi}/billaccounts/afi/by-waid/${this.getCustomerDetails().waid ?? ''}`
      : `${env.billingExpApi}/billaccounts/afi/by-waid`;

    // by-waid does NOT depend on the customer's policies. Fire it as soon
    // as we have headers — gating it behind `selectLoaded` (policies) would
    // delay or hang for users who never get a policy summary (e.g.
    // operator-only) and would leave the store empty.
    const byWaid$ = defer(() => {
      const headers = this.getHeaders();
      const options: { headers?: HttpHeaders; params?: HttpParams } = {};
      if (headers) options.headers = headers;
      if (this.isAdminApp) {
        const mdmId = this.mdmId();
        if (mdmId) {
          options.params = new HttpParams().set('mdmId', mdmId);
        }
      }
      return this.http.get(byWaidEndpoint, options);
    }).pipe(
      retry(1),
      catchError(() => of({ status: { code: 500 }, billAccounts: [] }))
    );

    // Wait for policy summaries to settle (loaded or errored) before snapshotting bill account
    // numbers. "Not loading" isn't enough: on first login this runs before the summaries load is
    // dispatched, when loading is still its initial false and the numbers are still []. Sessions
    // that will never load summaries (business accounts) pass straight through — see
    // selectPolicySummariesSettledOrSkipped. The timeout is a last resort for anything else; keep
    // it generous, since /policies alone has been observed taking ~13s in lower environments.
    const associatedBillAccounts$ = this.store.select(selectPolicySummariesSettledOrSkipped).pipe(
      filter(ready => ready),
      take(1),
      timeout(30000),
      catchError(err => {
        console.error('[BillAccountsService] policy-wait timed out or errored', err);
        return of(null);
      }),
      map(() => this.store.selectSignal(selectAfiMidvaleBillingAccountNumbers)()),
      switchMap(billAccountNumbers => {
        if (billAccountNumbers.length === 0) {
          return of({ status: { code: 200 }, billAccounts: [] });
        }
        const httpHeaders = this.getHeaders() || undefined;
        return forkJoin(
          billAccountNumbers.map(billAccountNumber => {
            const url = `${env.billingExpApi}/billaccounts/afi/${billAccountNumber}`;
            const params = this.isAdminApp ? this.buildAdminParams() : undefined;
            return this.http
              .get<{ billingAccount: BillAccount }>(url, {
                headers: httpHeaders,
                params
              })
              .pipe(catchError(() => of(null)));
          })
        ).pipe(
          map(responses => ({
            status: { code: 200 },
            billAccounts: responses
              .filter(response => response !== null)
              .map(response => response['billingAccount'])
              .filter(account => !!account)
          }))
        );
      }),
      catchError(() => of({ status: { code: 500 }, billAccounts: [] }))
    );

    // Always fetch both. `associatedBillAccounts$` short-circuits to an
    // empty result when the policy fan-out yields zero numbers — the cost
    // is just one synchronous selector read after `selectLoaded` flips,
    // which is negligible.
    return forkJoin([associatedBillAccounts$, byWaid$]);
  }
  /**
   *
   * @returns Supports only Phoenix and Horizon partners, AFI and Midvale use getBillAccountsAfiMidvale method temporarily till onboarded to EXP API truely.
   */
  public getBillAccountsFromExpApi() {
    const mdmId$ = this.isAdminApp ? this.store.select(selectMdmId).pipe(take(1)) : of(undefined);
    // v2 ENT bill-accounts endpoint. The route version selects the current
    // billing logic server-side, replacing the old `feature-toggle` query
    // param. Admin's consolidated route needs `v2` spliced BEFORE `billing`
    // (`.../admin/v2/billing/billaccounts`), not suffixed onto billingExpApi —
    // every other call in this file shares billingExpApi unversioned
    // (`.../admin/billing`), so it's built from expAdminApi instead, same as
    // bills.service.ts.
    const baseUrl = this.isAdminApp
      ? `${requireExpAdminApi(env.expAdminApi)}/v2/billing/billaccounts`
      : `${env.billingExpApi}/v2/billaccounts`;
    return mdmId$.pipe(
      switchMap(mdmId => {
        let params = new HttpParams();
        if (mdmId) params = params.set('mdmId', mdmId);
        return this.http.get(baseUrl, { params });
      })
    );
  }

  public getBillAccountsDocumentsFromExpApi() {
    const baseUrl = `${env.billingExpApi}/billaccounts/documents`;
    const mdmId$ = this.isAdminApp ? this.store.select(selectMdmId).pipe(take(1)) : of(undefined);
    return mdmId$.pipe(
      switchMap(mdmId => {
        const params = mdmId ? new HttpParams().set('mdmId', mdmId) : undefined;
        return this.http.get(baseUrl, { params });
      })
    );
  }

  /**
   * Get bill account details for a single account, by billAccountNumber.
   */
  public getBillAccount(payload: { billAccountNumber?: string; policyNumber?: string }) {
    const url = `${env.billingExpApi}/billaccounts/afi/${payload.billAccountNumber}`;
    const params = this.isAdminApp ? this.buildAdminParams() : undefined;

    return this.http.get<{ billingAccount: BillAccount }>(url, { params }).pipe(retry(1));
  }

  public getStatementHistory(billAccountNumber: string, billingSystem: string) {
    const mdmId$ = this.isAdminApp ? this.store.select(selectMdmId).pipe(take(1)) : of(undefined);

    return mdmId$.pipe(
      switchMap(mdmId => {
        let params = new HttpParams().set('billingSystem', billingSystem);
        if (mdmId) {
          params = params.set('mdmId', mdmId);
        }
        return this.http.get<{ billAccount: BillAccount }>(
          `${env.billingExpApi}/billaccounts/afi/${billAccountNumber}/statements`,
          { params }
        );
      })
    );
  }

  public canRegisterExp(
    accountNumber: string,
    policyNumber: string
  ): Observable<{
    billingAccount: BillAccount;
    billAccountNumber?: string;
    policyNumber?: string;
    canRegister?: boolean;
  }> {
    const url = `${env.billingExpApi}/billaccounts/afi/${accountNumber}`;
    let params;
    if (this.isAdminApp) {
      params = this.buildAdminParams();
    }

    return this.http
      .get<{
        billingAccount: BillAccount;
      }>(url, { params })
      .pipe(
        map(responseJson => {
          let result: {
            billingAccount: BillAccount;
            billAccountNumber?: string;
            policyNumber?: string;
            canRegister?: boolean;
          } = responseJson;
          try {
            result = {
              ...result,
              billAccountNumber: accountNumber,
              policyNumber: policyNumber,
              canRegister: result.billingAccount.eligibleBillingMethods?.some(
                m =>
                  m.billingMethod === BillAccountRegistration.ONLINE_BILLING &&
                  m.eligibleStatus === 'true'
              )
            };
          } catch {
            result = { ...result, canRegister: false };
          }
          return result;
        })
      );
  }

  public updateRegistration(payload: UpdateRegistrationRequest): Observable<boolean> {
    const registrationData = payload.registrationData;
    const isUnregister = payload.isUnregister;

    /**
     * billingPreference object is required in apiPayload when Registering bill account ONLY.
     */
    let billingPreference: BillingPreference | undefined;
    if (!isUnregister) {
      billingPreference = {
        billingPreference: {
          dueDateReminder: registrationData.reminder,
          accountNickname: registrationData.accountName
        }
      };
    }

    const apiPayload = {
      actionIndicator: isUnregister ? 'UNREGISTER' : 'REGISTER',
      policyNumber: registrationData.policyNumber,
      billAccount: {
        billingMethod: isUnregister ? 'UNSPECIFIED' : 'ONLINE BILLING',
        // Spread operator to add object to payload conditionally.
        ...(!isUnregister ? billingPreference : {})
      }
    };

    const url = `${env.billingExpApi}/billaccounts/afi/${payload.billAccountNumber}`;
    let params: HttpParams | undefined;
    if (this.isAdminApp) {
      params = this.buildAdminParams();
      const mdmId = this.mdmId();
      if (mdmId) {
        params = params.set('mdmId', mdmId);
      }
    }

    return this.http.put<UpdateRegistrationResponse>(url, apiPayload, { params }).pipe(
      map(response => this.decorateUpdateRegistrationCall(response, isUnregister)),
      catchError(err => throwError(() => err))
    );
  }

  public getPreferences(
    payload: RetrievePreferencePayload
  ): Observable<DecoratedPreference | ApiStatus> {
    let params;
    const { billAccountNumber } = payload;
    const url = `${env.billingExpApi}/billaccounts/afi/${billAccountNumber}/preferences`;
    if (this.isAdminApp) {
      params = this.buildAdminParams();
    }

    return this.http
      .get<RetrievePreferenceResponse>(url, { params })
      .pipe(
        map(preference => this.decorateRetrievePreferencesCall(preference, payload)),
        catchError(err => {
          if (err?.error?.status?.reason === 'Not Found' || err?.status === 404) {
            return of({ billAccountNumber, billingPreferences: {} } as DecoratedPreference);
          }
          return throwError(() => err);
        })
      )
      .pipe(retry(1));
  }

  // Updates billing preferences such as Account nick name, Duedate reminder .
  public updateBillingPreferences(
    updatePreferenceRequest: UpdatePreferencesRequest
  ): Observable<boolean | ApiStatus> {
    let params;
    const { billAccountNumber } = updatePreferenceRequest;
    const url = `${env.billingExpApi}/billaccounts/afi/${billAccountNumber}/preferences`;
    const payload = this.createExpApiPayload(updatePreferenceRequest);
    if (this.isAdminApp) {
      params = this.buildAdminParams();
    }

    return this.http.put<UpdatePreferencesResponse>(url, payload, { params }).pipe(
      map(updateBillingPreferencesResponse =>
        this.decorateUpdateBillingPreferencesCall(updateBillingPreferencesResponse.status)
      ),
      catchError(err => throwError(() => err))
    );
  }

  private createExpApiPayload(request: UpdatePreferencesRequest) {
    const preferences = request?.payload?.billingPreference?.preferences?.map(p => ({
      businessUnitCode: p.businessUnitCode,
      emailAddress: p.emailAddress,
      lastUpdateTimestamp: p.lastUpdateTimestamp,
      preferenceCode: p.preferenceCode,
      preferenceDeliveryCode: p.preferenceDeliveryCode,
      preferenceId: p.preferenceId,
      preferenceTypeCode: p.preferenceTypeCode
    }));
    const dueDateReminder = request?.payload?.billingPreference?.dueDateReminder;
    return {
      billingPreference: {
        accountNickname: request?.payload?.billingPreference?.accountNickname,
        dueDateReminder: dueDateReminder != null ? String(dueDateReminder) : dueDateReminder,
        preferences
      }
    };
  }

  // Updates delivery preferences such as paper, email or  both.
  public updateDeliveryPreference(
    updatePreferenceRequest: UpdatePreferencesRequest
  ): Observable<boolean | ApiStatus> {
    let params;
    const { billAccountNumber } = updatePreferenceRequest;
    const url = `${env.billingExpApi}/billaccounts/afi/${billAccountNumber}/preferences`;
    const payload = this.createExpApiPayload(updatePreferenceRequest);
    if (this.isAdminApp) {
      params = this.buildAdminParams();
    }

    return this.http.put<UpdatePreferencesResponse>(url, payload, { params }).pipe(
      map(updateDeliveryPreferenceResponse =>
        this.decorateUpdateDeliveryPreferenceCall(updateDeliveryPreferenceResponse.status)
      ),
      catchError(err => throwError(() => err))
    );
  }

  public deletePreferences(payload: DeletePreferencesRequest): Observable<boolean | unknown> {
    return this.http
      .delete<DeletePreferencesResponse>(
        this.getBillingApiPath() + 'billaccounts/' + payload.billAccountNumber + '/preferences'
      )
      .pipe(
        map(response => this.decorateDeletePreferencesCall(response)),
        catchError(err => throwError(() => err))
      );
  }

  public getUnAssociatedBillAccountRiskInformation(policyList: PolicyList): Observable<PolicyList> {
    let policies = '';
    policyList.forEach(policy => {
      policies = policies ? policies + ',' + policy.policyNumber : policy.policyNumber;
    });
    let params = new HttpParams().set('policyNumber', policies);
    if (this.isAdminApp) {
      const mdmId = this.mdmId();
      if (mdmId) {
        params = params.set('mdmId', mdmId);
      }
    }
    return this.http.get<UnassociatedRiskDetailSuccess>(this.payorOboPath(), { params }).pipe(
      map((response: UnassociatedRiskDetailSuccess) =>
        this.unassociatedBaRiskAdapter.adapt(response, policyList)
      ),
      catchError(error => throwError(() => error))
    );
  }

  private getBillingApiPath(): string {
    return env.billingApi;
  }

  /**
   * Get payor OBO API endpoint based on status of feature flag.
   * When `payorobo_exp_api` is enabled, routes to the customer-experience-billing
   * Experience API. Otherwise falls back to the legacy payorobo gateway endpoint.
   */
  private payorOboPath(): string {
    // Caller appends `?policyNumber=...` via HttpParams — return a clean
    // base URL with no query string suffix to avoid duplicate params.
    return this.featureFlagService.isEnabled('payorobo_exp_api')
      ? `${env.billingExpApi}/billaccounts/afi/payorobo/policies`
      : `${env.payoroboApi}policies`;
  }

  private decorateUpdateRegistrationCall(
    response: UpdateRegistrationResponse,
    isUnregister: boolean
  ): boolean {
    const code = Number(response.status?.code);
    if (code === 200) {
      // Return value is noPartialFailure
      return true;
    } else if (code === 207) {
      // The partial fail case is only for unregister
      if (!isUnregister) {
        return true;
      }
      // Get entity references and use to detect fail scenario
      const partialStatuses: PartialStatus[] = response.status.partialStatuses ?? [];
      const payloadEntityReferences: string[] = partialStatuses.map(
        status => status.payloadEntityReference
      );
      const preferenceFailed =
        payloadEntityReferences.includes(EntityReferences.PREFERENCES) ||
        payloadEntityReferences.includes(EntityReferences.PAPER) ||
        payloadEntityReferences.includes(EntityReferences.EMAIL);
      if (preferenceFailed) {
        // Unregister succeeded but preferences failed, we have a partial failure
        return false;
      }
      // Consider 207s successful if they do not meet the fail criteria
      return true;
    }
    // All other scenarios considered failure
    throw this.markAsError(response.status, 'Update registration error');
  }

  private buildAdminParams(): HttpParams {
    const { waid, cdhid } = this.getCustomerDetails();
    let params = new HttpParams();
    if (waid) {
      params = params.set('waid', waid);
    }
    if (cdhid) {
      params = params.set('cdhId', cdhid);
    }
    return params;
  }

  private getCustomerDetails(): {
    cdhid: string | undefined;
    waid: string | undefined;
    isCustomer: boolean | undefined;
  } {
    const user = this.userState();
    if (user && user.waid) {
      return { cdhid: user.customerId, waid: user.waid, isCustomer: user.isCustomer };
    }
    return { cdhid: undefined, waid: undefined, isCustomer: undefined };
  }

  private decorateRetrievePreferencesCall(
    preference: RetrievePreferenceResponse,
    billAccount: RetrievePreferencePayload
  ): DecoratedPreference | ApiStatus {
    const reason = preference.status.reason;
    if (reason === 'Not Found') {
      return {
        billAccountNumber: billAccount.billAccountNumber,
        billingPreferences: {}
      } as DecoratedPreference;
    }

    // Get status code.
    const code = Number(preference.status.code);
    if (code === 200) {
      return this.retrievepreferenceAdapter.adapt(preference);
    } else if (code === 207) {
      // billAccount should be associated.
      if (billAccount.associated) {
        const requiredPayloadEntityReferences: string[] = this.copyService.get(
          'billing',
          'requiredPayloadEntityReferences'
        );

        const partialStatuses: PartialStatus[] = preference.status.partialStatuses ?? [];

        // Extract payload entity references from API to match with requiredPayloadEntityReferences.
        const payloadEntityReferences: string[] = partialStatuses.map(
          status => status.payloadEntityReference
        );

        // Returns boolean if payloadEntityReferences from API matches with requiredPayloadEntityReferences(see copy file).
        const isPayloadEntityReferenceMatch = deepEqual(
          requiredPayloadEntityReferences.sort(),
          payloadEntityReferences.sort()
        );

        // billing method is online billing
        if (billAccount.billingMethod === ONLINE_BILLING) {
          // internal status code should be 403
          const internalCodeMatch = !!partialStatuses.find(
            partialStatus => partialStatus.status.code === 403 || partialStatus.status.code === 404
          );

          if (isPayloadEntityReferenceMatch && internalCodeMatch) {
            return this.retrievepreferenceAdapter.adapt(preference);
          }
        } else {
          // billing method is not online billing
          if (isPayloadEntityReferenceMatch) {
            return this.retrievepreferenceAdapter.adapt(preference);
          }
        }
      }
    }

    throw this.markAsError(preference.status, 'Retrieve preferences partial error');
  }

  private decorateUpdateBillingPreferencesCall(status: ApiStatus): boolean {
    const code = Number(status.code);

    if (code === 200) {
      return true;
    } else if (code === 207) {
      const requiredPayloadEntityReferences: string[] = this.copyService.get(
        'billing',
        'requiredPayloadEntityReferences'
      );

      const partialStatuses: PartialStatus[] = status.partialStatuses ?? [];

      // Extract payload entity references from API to match with requiredPayloadEntityReferences.
      const payloadEntityReferences: string[] = partialStatuses.map(
        partialStatus => partialStatus.payloadEntityReference
      );

      // Returns boolean if payloadEntityReferences from API matches with requiredPayloadEntityReferences(see copy file).
      const isPayloadEntityReferenceMatch = deepEqual(
        requiredPayloadEntityReferences.sort(),
        payloadEntityReferences.sort()
      );
      if (isPayloadEntityReferenceMatch) {
        throw this.markAsError(status, 'Update billing preferences partial error');
      } else {
        return true;
      }
    }
    throw this.markAsError(status, 'Update billing preferences partial error');
  }

  private decorateUpdateDeliveryPreferenceCall(status: ApiStatus): boolean {
    const code = Number(status.code);
    if (code === 200) {
      return true;
    } else if (code === 207) {
      const requiredPayloadEntityReferences: string[] = this.copyService.get(
        'billing',
        'requiredPayloadEntityReferences'
      );

      const partialStatuses: PartialStatus[] = status.partialStatuses ?? [];

      // Extract payload entity references from API to match with requiredPayloadEntityReferences.
      const payloadEntityReferences: string[] = partialStatuses.map(
        partialStatus => partialStatus.payloadEntityReference
      );

      // Returns boolean if payloadEntityReferences from API matches with requiredPayloadEntityReferences(see copy file).
      const isPayloadEntityReferenceMatch = deepEqual(
        requiredPayloadEntityReferences.sort(),
        payloadEntityReferences.sort()
      );
      if (isPayloadEntityReferenceMatch) {
        return true;
      }
    }
    throw this.markAsError(status, 'Update delivery preferences partial error');
  }

  private decorateDeletePreferencesCall(response: DeletePreferencesResponse): boolean {
    // TODO: this function doesn't need to return anything, Abhi has ideas for the re-write
    const code = Number(response.status.code);
    if (code === 200) {
      // Return value is whether or not call was successful
      return true;
    } else if (code === 207) {
      // Get entity references and use to detect fail scenario
      const partialStatuses: PartialStatus[] = response.status.partialStatuses ?? [];
      const payloadEntityReferences: string[] = partialStatuses.map(
        status => status.payloadEntityReference
      );
      const isFail =
        payloadEntityReferences.includes(EntityReferences.PAPER) ||
        payloadEntityReferences.includes(EntityReferences.EMAIL);
      if (isFail) {
        throw this.markAsError(response.status, 'Delete preferences partial error');
      }
      // Consider 207s successful if they do not meet the fail criteria
      return true;
    }
    // All other scenarios considered failure
    throw this.markAsError(response.status, 'Delete preferences error');
  }

  private markAsError(statusObject: ApiStatus, reason: string): ApiStatus {
    return { ...statusObject, code: 500, reason: reason };
  }
}
